import { Smile } from 'lucide-react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import {
  EMOJI_CATEGORIES,
  type EmojiCategory,
} from '@/shared/constants/emojis';
import { cn } from '@/shared/lib/utils';
import { ButtonSize, ButtonVariant } from '@/shared/types/button.types';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { ScrollArea } from './scroll-area';

/**
 * Props for EmojiPicker component
 */
export interface EmojiPickerProps {
  /** Currently selected emoji */
  readonly value?: string;
  /** Callback when emoji is selected */
  readonly onChange: (emoji: string) => void;
  /** Categories to show (defaults to all) */
  readonly categories?: readonly EmojiCategory[];
  /** Number of recent emojis to track (0 to disable) */
  readonly recentCount?: number;
  /** Additional CSS classes for the trigger */
  readonly className?: string;
  /** Whether the picker is disabled */
  readonly disabled?: boolean;
  /** Placeholder when no emoji is selected */
  readonly placeholder?: string;
}

/**
 * Category display names in Bulgarian
 */
const CATEGORY_LABELS: Readonly<Record<EmojiCategory, string>> = {
  events: 'Събития',
  activities: 'Дейности',
  family: 'Семейство',
  status: 'Статус',
  weather: 'Време',
  food: 'Храна',
  education: 'Образование',
  health: 'Здраве',
  travel: 'Пътуване',
  household: 'Дом',
  finance: 'Финанси',
  social: 'Социални',
  nature: 'Природа',
  common: 'Често използвани',
} as const;

/**
 * Default categories to show if none specified
 */
const DEFAULT_CATEGORIES: readonly EmojiCategory[] = [
  'events',
  'activities',
  'family',
  'status',
  'common',
] as const;

/**
 * EmojiPicker - ASCII emoji selection component
 *
 * Provides a popover interface for selecting emojis from predefined categories.
 * Uses the centralized EMOJI_CATEGORIES from shared constants.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <EmojiPicker
 *   value={selectedEmoji}
 *   onChange={setSelectedEmoji}
 * />
 *
 * // With specific categories
 * <EmojiPicker
 *   value={emoji}
 *   onChange={setEmoji}
 *   categories={['events', 'activities', 'family']}
 * />
 * ```
 */
export const EmojiPicker = forwardRef<HTMLButtonElement, EmojiPickerProps>(
  (
    {
      value,
      onChange,
      categories = DEFAULT_CATEGORIES,
      recentCount = 8,
      className,
      disabled = false,
      placeholder = 'Избери',
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [recentEmojis, setRecentEmojis] = useState<string[]>([]);
    const [activeCategory, setActiveCategory] = useState<
      EmojiCategory | 'recent'
    >(
      recentCount > 0 && recentEmojis.length > 0
        ? 'recent'
        : (categories[0] ?? 'events')
    );

    // Get emojis for active category
    const activeEmojis = useMemo(() => {
      if (activeCategory === 'recent') {
        return recentEmojis;
      }
      return EMOJI_CATEGORIES[activeCategory] ?? [];
    }, [activeCategory, recentEmojis]);

    // Handle emoji selection
    const handleSelect = useCallback(
      (emoji: string) => {
        onChange(emoji);
        setOpen(false);

        // Add to recent emojis
        if (recentCount > 0) {
          setRecentEmojis((prev) => {
            const filtered = prev.filter((e) => e !== emoji);
            return [emoji, ...filtered].slice(0, recentCount);
          });
        }
      },
      [onChange, recentCount]
    );

    // Clear selection
    const handleClear = useCallback(() => {
      onChange('');
      setOpen(false);
    }, [onChange]);

    // Get available categories with recent
    const availableCategories = useMemo(() => {
      const cats: Array<EmojiCategory | 'recent'> = [];
      if (recentCount > 0 && recentEmojis.length > 0) {
        cats.push('recent');
      }
      cats.push(...categories);
      return cats;
    }, [categories, recentCount, recentEmojis.length]);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant={ButtonVariant.Outline}
            disabled={disabled}
            className={cn('w-auto min-w-[80px] gap-2', className)}
            aria-label="Избери емоджи"
          >
            {value ? (
              <span className="text-lg">{value}</span>
            ) : (
              <>
                <Smile
                  className="size-4 text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="text-muted-foreground">{placeholder}</span>
              </>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[320px] p-0" align="start">
          {/* Category tabs */}
          <div className="flex overflow-x-auto border-b border-border">
            {availableCategories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={cn(
                  'whitespace-nowrap px-3 py-2 text-xs font-medium',
                  'hover:bg-accent hover:text-accent-foreground',
                  'focus:bg-accent focus:outline-none',
                  activeCategory === category
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted-foreground'
                )}
              >
                {category === 'recent' ? 'Скорошни' : CATEGORY_LABELS[category]}
              </button>
            ))}
          </div>

          {/* Emoji grid */}
          <ScrollArea className="h-[200px]">
            <div className="grid grid-cols-8 gap-1 p-2">
              {activeEmojis.map((emoji, index) => (
                <button
                  key={`${emoji}-${index}`}
                  type="button"
                  onClick={() => handleSelect(emoji)}
                  className={cn(
                    'flex size-8 items-center justify-center rounded',
                    'text-lg hover:bg-accent focus:bg-accent focus:outline-none',
                    value === emoji && 'bg-accent ring-1 ring-primary'
                  )}
                  aria-label={`Избери ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
              {activeEmojis.length === 0 && (
                <div className="col-span-8 py-8 text-center text-sm text-muted-foreground">
                  {activeCategory === 'recent'
                    ? 'Няма скорошни емоджита'
                    : 'Няма емоджита в тази категория'}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-border p-2">
            <span className="text-xs text-muted-foreground">
              {value ? `Избрано: ${value}` : 'Няма избрано'}
            </span>
            {value && (
              <Button
                variant={ButtonVariant.Ghost}
                size={ButtonSize.Small}
                onClick={handleClear}
                className="h-7 text-xs"
              >
                Изчисти
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

EmojiPicker.displayName = 'EmojiPicker';
